import React, { useEffect, useMemo, useState } from "react";
import { useAuthenticatedApi } from "../../hooks/useAuthenticatedApi";
import { apiEndpoints } from "../../utils/api";
import { Spinner } from "../../design-system";

interface FeatureFlag {
  key: string;
  description?: string;
  enabled: boolean;
  allowed_tiers?: string[];
  created_at?: string;
  updated_at?: string;
}

interface UserOverrides {
  user_id: string;
  enabled_features: string[];
  disabled_features: string[];
  created_at?: string;
  updated_at?: string;
}

const FeatureFlagsSection: React.FC = () => {
  const api = useAuthenticatedApi();
  const [flags, setFlags] = useState<FeatureFlag[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [flagSaved, setFlagSaved] = useState(false);

  const [editingFlag, setEditingFlag] = useState<FeatureFlag | null>(null);
  const [overrideModalOpen, setOverrideModalOpen] = useState(false);
  const [overrideModalSaving, setOverrideModalSaving] = useState(false);
  const [overrideModalError, setOverrideModalError] = useState<string | null>(
    null,
  );
  const [overrideModalData, setOverrideModalData] = useState<{
    user_id: string;
    enabled: string;
    disabled: string;
  } | null>(null);
  const [overridesDeletedFor, setOverridesDeletedFor] = useState<string | null>(
    null,
  );
  const [confirmState, setConfirmState] = useState<
    | { type: "delete-flag"; key: string }
    | { type: "delete-overrides"; userId: string }
    | null
  >(null);
  const [confirming, setConfirming] = useState(false);
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [confirmModalTitle, setConfirmModalTitle] = useState<string>("");
  const [confirmModalMessage, setConfirmModalMessage] =
    useState<React.ReactNode>("");
  const [overridesList, setOverridesList] = useState<UserOverrides[]>([]);
  const [overridesListLoading, setOverridesListLoading] = useState(false);

  const tierOptions = useMemo(() => ["free", "starter", "pro", "team"], []);
  const deleteFlag = (key: string) => {
    setConfirmState({ type: "delete-flag", key });
    setConfirmModalTitle("Delete Feature Flag");
    setConfirmModalMessage(
      <>
        Are you sure you want to delete the flag{" "}
        <span className="font-semibold">{key}</span>? This cannot be undone.
      </>,
    );
    setConfirmModalOpen(true);
  };

  const fetchFlags = async () => {
    if (!api) return;
    setLoading(true);
    setError(null);
    try {
      const list = await api.get<FeatureFlag[]>(
        apiEndpoints.features.admin.listFlags,
      );
      setFlags(list || []);
    } catch (e: any) {
      setError(e?.message || "Failed to load feature flags");
    } finally {
      setLoading(false);
    }
  };

  const fetchOverridesList = async () => {
    if (!api) return;
    setOverridesListLoading(true);
    try {
      const resp = await api.get<{ items: UserOverrides[]; total: number }>(
        apiEndpoints.features.admin.listUserOverrides(),
      );
      setOverridesList(resp.items || []);
    } catch (e) {
      // silent; page has other error UIs
    } finally {
      setOverridesListLoading(false);
    }
  };

  const saveFlag = async (flag: FeatureFlag) => {
    if (!api) return;
    setError(null);
    try {
      const saved = await api.put<FeatureFlag>(
        apiEndpoints.features.admin.upsertFlag(flag.key),
        {
          description: flag.description || null,
          enabled: !!flag.enabled,
          allowed_tiers:
            flag.allowed_tiers && flag.allowed_tiers.length > 0
              ? flag.allowed_tiers
              : null,
        },
      );
      // Replace or add
      setFlags((prev) => {
        const idx = prev.findIndex((f) => f.key === saved.key);
        if (idx >= 0) {
          const copy = prev.slice();
          copy[idx] = saved;
          return copy;
        }
        return [...prev, saved];
      });
      setEditingFlag(null);
      setFlagSaved(true);
      window.setTimeout(() => setFlagSaved(false), 2000);
    } catch (e: any) {
      setError(e?.message || "Failed to save flag");
    }
  };

  const openAddOverride = () => {
    setOverrideModalError(null);
    setOverrideModalData({ user_id: "", enabled: "", disabled: "" });
    setOverrideModalOpen(true);
  };

  const openEditOverride = (u: UserOverrides) => {
    setOverrideModalError(null);
    setOverrideModalData({
      user_id: u.user_id,
      enabled: (u.enabled_features || []).join(","),
      disabled: (u.disabled_features || []).join(","),
    });
    setOverrideModalOpen(true);
  };

  const resetOverrideModal = () => {
    setOverrideModalOpen(false);
    setOverrideModalSaving(false);
    setOverrideModalError(null);
    setOverrideModalData(null);
  };

  const saveOverrideModal = async () => {
    if (!api || !overrideModalData) return;
    setOverrideModalError(null);
    setOverrideModalSaving(true);
    try {
      const enabledArr = overrideModalData.enabled
        .split(",")
        .map((s) => s.trim())
        .filter(Boolean);
      const disabledArr = overrideModalData.disabled
        .split(",")
        .map((s) => s.trim())
        .filter(Boolean);
      await api.put<UserOverrides>(
        apiEndpoints.features.admin.upsertUserOverrides(
          overrideModalData.user_id,
        ),
        { enabled_features: enabledArr, disabled_features: disabledArr },
      );
      await fetchOverridesList();
      resetOverrideModal();
    } catch (e: any) {
      setOverrideModalError(e?.message || "Failed to save overrides");
      setOverrideModalSaving(false);
    }
  };

  useEffect(() => {
    fetchFlags();
    fetchOverridesList();
  }, [api]);

  return (
    <div className="p-6">
      <div className="mb-6">
        <h2 className="text-2xl font-semibold text-ui-text-primary mb-2">
          Feature Flags
        </h2>
        <p className="text-ui-text-secondary">
          Manage global flags and per-user overrides.
        </p>
      </div>

      {/* Flags list */}
      <div className="mb-8">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-medium text-ui-text-primary">
            Flags
          </h3>
          <button
            className="px-3 py-2 rounded-md bg-brand-primary text-black text-sm"
            onClick={() =>
              setEditingFlag({
                key: "",
                description: "",
                enabled: false,
                allowed_tiers: [],
              })
            }
          >
            New Flag
          </button>
        </div>

        {loading ? (
          <div className="text-ui-text-secondary">
            Loading...
          </div>
        ) : error ? (
          <div className="text-red-600">{error}</div>
        ) : (
          <div className="space-y-2">
            {flags.map((f) => (
              <div
                key={f.key}
                className="flex items-center justify-between p-4 rounded-lg border border-ui-border-primary bg-ui-background-secondary"
              >
                <div>
                  <div className="font-medium text-ui-text-primary">
                    {f.key}
                  </div>
                  {f.description && (
                    <div className="text-sm text-ui-text-secondary">
                      {f.description}
                    </div>
                  )}
                  <div className="text-xs text-ui-text-tertiary">
                    Allowed tiers: {f.allowed_tiers?.join(", ") || "all"}
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <span
                    className={`text-xs px-2 py-1 rounded ${f.enabled ? "bg-green-100 text-green-800" : "bg-neutral-200 text-ui-text-secondary dark:bg-ui-background-tertiary dark:text-ui-text-tertiary"}`}
                  >
                    {f.enabled ? "Enabled" : "Disabled"}
                  </span>
                  <button
                    className="px-2 py-1 text-sm rounded-md border border-ui-border-primary text-ui-text-secondary"
                    onClick={() => setEditingFlag(f)}
                  >
                    Edit
                  </button>
                  <button
                    className="px-2 py-1 text-sm rounded-md border border-red-300 text-red-700 dark:border-red-700 dark:text-red-400"
                    onClick={() => deleteFlag(f.key)}
                  >
                    Delete
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        {flagSaved && (
          <div className="mt-3 p-3 bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-400 rounded-lg text-sm">
            Flag saved successfully
          </div>
        )}
      </div>

      {/* Edit/Create flag modal (simple inline panel) */}
      {editingFlag && (
        <div className="mb-8 p-4 rounded-lg border border-ui-border-primary bg-ui-background-secondary">
          <div className="mb-3 font-medium text-ui-text-primary">
            {editingFlag.key ? "Edit Flag" : "Create Flag"}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm mb-1 text-ui-text-secondary">
                Key
              </label>
              <input
                className="w-full px-3 py-2 rounded-md border border-ui-border-primary bg-ui-background-secondary text-ui-text-primary"
                value={editingFlag.key}
                onChange={(e) =>
                  setEditingFlag({ ...editingFlag, key: e.target.value.trim() })
                }
                placeholder="e.g. next-editor"
              />
            </div>
            <div>
              <label className="block text-sm mb-1 text-ui-text-secondary">
                Enabled
              </label>
              <input
                type="checkbox"
                className="h-4 w-4"
                checked={!!editingFlag.enabled}
                onChange={(e) =>
                  setEditingFlag({ ...editingFlag, enabled: e.target.checked })
                }
              />
            </div>
            <div className="md:col-span-2">
              <label className="block text-sm mb-1 text-ui-text-secondary">
                Description
              </label>
              <input
                className="w-full px-3 py-2 rounded-md border border-ui-border-primary bg-ui-background-secondary text-ui-text-primary"
                value={editingFlag.description || ""}
                onChange={(e) =>
                  setEditingFlag({
                    ...editingFlag,
                    description: e.target.value,
                  })
                }
                placeholder="Describe the feature"
              />
            </div>
            <div className="md:col-span-2">
              <label className="block text-sm mb-1 text-ui-text-secondary">
                Allowed Tiers
              </label>
              <div className="flex flex-wrap gap-3">
                {tierOptions.map((t) => {
                  const selected = (editingFlag.allowed_tiers || []).includes(
                    t,
                  );
                  return (
                    <label
                      key={t}
                      className="flex items-center gap-2 text-sm text-ui-text-secondary"
                    >
                      <input
                        type="checkbox"
                        checked={selected}
                        onChange={(e) => {
                          const current = new Set(
                            editingFlag.allowed_tiers || [],
                          );
                          if (e.target.checked) current.add(t);
                          else current.delete(t);
                          setEditingFlag({
                            ...editingFlag,
                            allowed_tiers: Array.from(current),
                          });
                        }}
                      />
                      {t}
                    </label>
                  );
                })}
              </div>
              <div className="text-xs text-ui-text-tertiary mt-1">
                Leave empty for all tiers.
              </div>
            </div>
          </div>
          <div className="mt-4 flex items-center gap-3">
            <button
              className="px-3 py-2 rounded-md bg-brand-primary text-black text-sm"
              onClick={() => saveFlag(editingFlag)}
            >
              Save
            </button>
            <button
              className="px-3 py-2 rounded-md border border-ui-border-primary text-sm text-ui-text-secondary"
              onClick={() => setEditingFlag(null)}
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* User Overrides */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <h4 className="text-md font-medium text-ui-text-primary">
            User Overrides
          </h4>
          <button
            className="px-3 py-2 rounded-md bg-brand-primary text-black text-sm"
            onClick={openAddOverride}
          >
            Add User Override
          </button>
        </div>
        {overridesDeletedFor && (
          <div className="mb-4 p-3 border border-green-300 dark:border-green-800 bg-green-50 dark:bg-green-900/20 text-green-800 dark:text-green-300 rounded-lg text-sm">
            User overrides deleted for ID:{" "}
            <span className="font-mono">{overridesDeletedFor}</span>
          </div>
        )}
        {/* Overrides Table */}
        <div className="mb-6">
          {overridesListLoading ? (
            <div className="text-ui-text-secondary">
              Loading list…
            </div>
          ) : overridesList.length === 0 ? (
            <div className="text-sm text-ui-text-secondary">
              No overrides found.
            </div>
          ) : (
            <div className="overflow-x-auto border border-ui-border-primary rounded-lg">
              <table className="min-w-full text-sm">
                <thead className="bg-ui-background-secondary text-ui-text-secondary">
                  <tr>
                    <th className="text-left px-3 py-2">User ID</th>
                    <th className="text-left px-3 py-2">Enabled</th>
                    <th className="text-left px-3 py-2">Disabled</th>
                    <th className="text-right px-3 py-2">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {overridesList.map((u) => (
                    <tr
                      key={u.user_id}
                      className="border-t border-ui-border-primary"
                    >
                      <td className="px-3 py-2 font-mono text-ui-text-primary">
                        {u.user_id}
                      </td>
                      <td className="px-3 py-2 text-ui-text-secondary truncate">
                        {u.enabled_features.join(", ") || "-"}
                      </td>
                      <td className="px-3 py-2 text-ui-text-secondary truncate">
                        {u.disabled_features.join(", ") || "-"}
                      </td>
                      <td className="px-3 py-2 text-right">
                        <button
                          className="px-2 py-1 text-xs rounded-md border border-ui-border-primary text-ui-text-secondary mr-2"
                          onClick={() => openEditOverride(u)}
                        >
                          Edit
                        </button>
                        <button
                          className="px-2 py-1 text-xs rounded-md border border-red-300 text-red-700 dark:border-red-700 dark:text-red-400"
                          onClick={() => {
                            setConfirmState({
                              type: "delete-overrides",
                              userId: u.user_id,
                            });
                            setConfirmModalTitle("Delete User Overrides");
                            setConfirmModalMessage(
                              <>
                                This will remove all overrides for user ID{" "}
                                <span className="font-semibold">
                                  {u.user_id}
                                </span>
                                . The user will fall back to tier-based
                                defaults. Proceed?
                              </>,
                            );
                            setConfirmModalOpen(true);
                          }}
                        >
                          Delete
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
        {/* Add/Edit Override Modal */}
        {overrideModalOpen && overrideModalData && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-ui-background-secondary rounded-xl max-w-xl w-full">
              <div className="p-6 border-b border-ui-border-primary">
                <h3 className="text-lg font-semibold text-ui-text-primary">
                  {overridesList.some(
                    (o) =>
                      o.user_id === overrideModalData.user_id &&
                      overrideModalData.user_id,
                  )
                    ? "Edit User Override"
                    : "Add User Override"}
                </h3>
              </div>
              <div className="p-6 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-ui-text-secondary mb-1">
                    User ID *
                  </label>
                  <input
                    type="text"
                    value={overrideModalData.user_id}
                    onChange={(e) =>
                      setOverrideModalData({
                        ...(overrideModalData || {
                          user_id: "",
                          enabled: "",
                          disabled: "",
                        }),
                        user_id: e.target.value.trim(),
                      })
                    }
                    className={`w-full px-3 py-2 border border-ui-border-primary rounded-lg ${overrideModalData.user_id ? "bg-ui-background-secondary text-ui-text-primary" : "bg-ui-background-secondary text-ui-text-primary"}`}
                    placeholder="UUID of the user"
                    disabled={overridesList.some(
                      (o) => o.user_id === overrideModalData.user_id,
                    )}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-ui-text-secondary mb-1">
                    Enabled Features (comma-separated)
                  </label>
                  <input
                    type="text"
                    value={overrideModalData.enabled}
                    onChange={(e) =>
                      setOverrideModalData({
                        ...(overrideModalData || {
                          user_id: "",
                          enabled: "",
                          disabled: "",
                        }),
                        enabled: e.target.value,
                      })
                    }
                    className="w-full px-3 py-2 border border-ui-border-primary rounded-lg bg-ui-background-secondary text-ui-text-primary"
                    placeholder="e.g., next-editor,experimental-x"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-ui-text-secondary mb-1">
                    Disabled Features (comma-separated)
                  </label>
                  <input
                    type="text"
                    value={overrideModalData.disabled}
                    onChange={(e) =>
                      setOverrideModalData({
                        ...(overrideModalData || {
                          user_id: "",
                          enabled: "",
                          disabled: "",
                        }),
                        disabled: e.target.value,
                      })
                    }
                    className="w-full px-3 py-2 border border-ui-border-primary rounded-lg bg-ui-background-secondary text-ui-text-primary"
                    placeholder="e.g., legacy-mode"
                  />
                </div>
                {overrideModalError && (
                  <div className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
                    <p className="text-sm text-red-600 dark:text-red-400">
                      {overrideModalError}
                    </p>
                  </div>
                )}
                <div className="flex items-center justify-end gap-3 pt-2">
                  <button
                    type="button"
                    onClick={resetOverrideModal}
                    className="px-4 py-2 text-ui-text-secondary hover:text-ui-text-primary transition-colors"
                    disabled={overrideModalSaving}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={saveOverrideModal}
                    disabled={overrideModalSaving || !overrideModalData.user_id}
                    className={`bg-brand-primary text-black px-4 py-2 rounded-lg hover:bg-brand-primary/90 transition-colors flex items-center gap-2 ${overrideModalSaving ? "opacity-60 cursor-not-allowed" : ""}`}
                  >
                    {overrideModalSaving && (
                      <Spinner size={16} tone="current" />
                    )}
                    {overrideModalSaving ? "Saving…" : "Save"}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Confirmation Modal */}
        {confirmModalOpen && confirmState && (
          <div
            className="fixed inset-0 bg-black/30 dark:bg-black/60 flex items-center justify-center z-50"
            onClick={(e) => {
              if (e.target === e.currentTarget) setConfirmModalOpen(false);
            }}
          >
            <div
              className="bg-ui-background-secondary rounded-xl shadow-xl max-w-md w-full mx-4 overflow-hidden"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="px-6 py-4 border-b border-ui-border-primary">
                <h2 className="text-lg font-semibold text-ui-text-primary">
                  {confirmModalTitle}
                </h2>
              </div>
              <div className="p-6">
                <div className="text-ui-text-secondary text-sm">
                  {confirmModalMessage}
                </div>
                <div className="flex justify-end space-x-3 mt-6">
                  <button
                    onClick={() => {
                      setConfirmState(null);
                      setConfirmModalOpen(false);
                    }}
                    disabled={confirming}
                    className="px-4 py-2 text-sm rounded-lg bg-ui-background-tertiary text-ui-text-secondary hover:bg-ui-background-tertiary transition-colors disabled:opacity-50"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={async () => {
                      if (!api || !confirmState) return;
                      setConfirming(true);
                      try {
                        if (confirmState.type === "delete-flag") {
                          await api.delete(
                            apiEndpoints.features.admin.deleteFlag(
                              confirmState.key,
                            ),
                          );
                          setFlags((prev) =>
                            prev.filter((f) => f.key !== confirmState.key),
                          );
                        } else if (confirmState.type === "delete-overrides") {
                          await api.delete(
                            apiEndpoints.features.admin.deleteUserOverrides(
                              confirmState.userId,
                            ),
                          );
                          setOverridesDeletedFor(confirmState.userId);
                          window.setTimeout(
                            () => setOverridesDeletedFor(null),
                            2500,
                          );
                          fetchOverridesList();
                        }
                        setConfirmModalOpen(false);
                        setConfirmState(null);
                      } catch (e: any) {
                        setError(e?.message || "Operation failed");
                      } finally {
                        setConfirming(false);
                      }
                    }}
                    disabled={confirming}
                    className="px-4 py-2 text-sm rounded-lg bg-red-600 text-white hover:bg-red-700 transition-colors disabled:opacity-50"
                  >
                    {confirming ? "Working..." : "Delete"}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default FeatureFlagsSection;
